<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div
      class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25"
    >
      <div class="d-sm-flex align-items-center" />
      <div class="d-flex align-items-center">
        <form class="search-box position-relative me-15" @submit.prevent="rechercher">
          <input
            v-model="searchTerm"
            type="text"
            class="form-control shadow-none text-black rounded-0 border-0"
            placeholder="Code place, marchand, référence, désignation…"
            @keyup="rechercher"
          />
          <button type="submit" class="bg-transparent text-primary transition p-0 border-0">
            <i class="flaticon-search-interface-symbol"></i>
          </button>
        </form>
      </div>
    </div>

    <div class="card-body p-15 p-sm-20 p-md-25">
      <div class="table-responsive">
        <table class="table text-nowrap align-middle mb-0">
          <thead>
            <tr>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Code place
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Marchand
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Réf. contrat
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Désignation
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Quantité
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Prix unit.
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Montant ligne
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Groupe de taxe
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                État
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                Date
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in rows" :key="index">
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ row?.contrat?.codePlace }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ row?.contrat?.nomMarchand }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ row?.contrat?.reference }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ row?.element?.designation }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ row?.quantite }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ row?.prix }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ row?.montant }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ row?.groupeTaxe?.libelle }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ libelleEtat(row?.etatElement) }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                {{ format_date(row?.createdAt) }}
              </td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis text-end">
                <router-link
                  v-if="row?.contrat?.id"
                  :to="`/view-contrat/${row.contrat.id}`"
                  class="btn btn-sm btn-outline-primary"
                >
                  Voir contrat
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        class="pagination-area d-md-flex mt-15 mt-sm-20 mt-md-25 justify-content-between align-items-center"
      >
        <PaginationComponent
          :page="page"
          :totalPages="totalPages"
          :totalElements="totalElements"
          :limit="limit"
          @paginate="handlePaginate"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import ApiService from "@/services/ApiService";
import { error, format_date } from "@/utils/utils";
import PaginationComponent from "@/components/Utilities/Pagination.vue";

export default defineComponent({
  name: "ListeContratElements",
  components: { PaginationComponent },
  setup() {

    const rows = ref<any[]>([]);
    const page = ref(1);
    const limit = ref(10);
    const totalPages = ref(1);
    const totalElements = ref(0);
    const searchTerm = ref("");

    const libelleEtat = (etat: number | undefined) => {
      if (etat === 2) return "Suspendu";
      return "Actif";
    };

    function fetchRows(pageNum = 1, lim = 10, mot = "") {
      ApiService.setHeader();
      return ApiService.query("all/contrat-elements", {
        params: { page: pageNum, limit: lim, mot },
      })
        .then(({ data }) => {
          console.log("Mes données",data.data);
          rows.value = data.data.data;
          totalPages.value = data.data.totalPages;
          limit.value = data.data.limit;
          totalElements.value = data.data.totalElements;
        })
        .catch((err: any) => {
          error(err?.response?.data?.message ?? "Erreur lors du chargement.");
        });
    }

    const handlePaginate = (newPage: object) => {
      page.value = newPage["page_"];
      fetchRows(page.value, limit.value, searchTerm.value);
    };

    const rechercher = () => {
      page.value = 1;
      fetchRows(page.value, limit.value, searchTerm.value);
    };

    onMounted(() => {
      fetchRows();
      console.log("Mounted, données chargées:");
    });

    return {
      rows,
      page,
      limit,
      totalPages,
      totalElements,
      searchTerm,
      format_date,
      libelleEtat,
      handlePaginate,
      rechercher,
    };
  },
});
</script>
